<template>
  <v-container class="pa-0 ma-0">
    <!-- Authorisation -->
    <v-card ref="authorisation" class="my-4 main">
      <!-- Header -->
      <v-card-title class="cardTitle border-b-thin border-b-md pa-0 pr-4">
        <v-card-actions>
          <v-icon
            :color="isAuthorisationValid ? 'green' : '#FB8C00'"
            class="ma-2"
          >
            {{ isAuthorisationValid ? "mdi-check-circle" : "mdi-alert" }}
          </v-icon>
          <div>Authorisation</div>
        </v-card-actions>
      </v-card-title>

      <!-- Content under the panel -->
      <v-card-text class="pb-3">
        <div class="text caption mt-3">
          To access the Datastore API, you need an API key to keep your requests
          secure and verified. Paste an existing token here if you already have
          one, or create a new token to proceed.
        </div>
        <!-- textbox -->
        <v-text-field
          v-model="selectedKey"
          class="mt-4"
          variant="outlined"
          density="compact"
          rounded="0"
          hide-details="true"
          label="API Key"
          autocomplete="off"
        ></v-text-field>
        <!-- Toggle and Button container -->
        <div class="d-flex align-items-center justify-space-between">
          <v-switch
            v-model="skipForNow"
            hide-details="true"
            label="Skip for now"
            density="compact"
            class="mt-4 ml-2"
          ></v-switch>
          <v-btn
            @click="$router.push('/datastore/api-keys')"
            variant="text"
            prepend-icon="mdi-plus"
            color="#0e5b99"
            rounded="0"
            class="create-api-key-btn mt-4"
          >
            Create API Key
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Indicator -->
    <v-card ref="indicator" class="my-4 main">
      <!-- Header -->
      <v-card-title class="cardTitle border-b-thin border-b-md pa-0 pr-4">
        <v-card-actions>
          <v-icon
            :color="isIndicatorComplete ? 'green' : '#FB8C00'"
            class="ma-2"
          >
            {{ isIndicatorComplete ? "mdi-check-circle" : "mdi-alert" }}
          </v-icon>
          <div>Indicator</div>
        </v-card-actions>
      </v-card-title>

      <!-- No indicator selected -->
      <v-card-text v-if="!selectedIndicator" class="text-left pa-4">
        <!-- Alert -->
        <v-alert color="info" icon="mdi-information">
          <div style="font-weight: 500">Add an indicator</div>
          <div>Add an indicator to build your query</div>
        </v-alert>
        <div class="button-container">
          <v-btn
            class="mt-4 create-api-key-btn"
            variant="elevated"
            prepend-icon="mdi-plus"
            color="#0e5b99"
            @click="$router.push('/datastore/catalogue')"
            rounded="0"
          >
            add indicator from catalogue
          </v-btn>
        </div>
      </v-card-text>

      <!-- Selected indicator -->
      <v-card-text v-else class="text-left pa-4">
        <v-card-actions class="pa-0">
          <div>
            <div class="text-subtitle-1" style="font-weight: 400">
              {{ selectedIndicator.indicator_name }}
            </div>
            <div class="text-caption" style="color: gray">
              <v-icon icon="mdi-code-tags" size="15px" />
              {{ selectedIndicator.indicator_code }}
            </div>
          </div>
          <v-spacer />
          <div>
            <v-icon
              @click="removeIndicatorDialog = true"
              icon="mdi-delete"
              color="error"
            />
          </div>
        </v-card-actions>

        <!-- LA only indicator warning -->
        <v-alert
          v-if="LAOnlyIndicator"
          class=""
          color="info"
          icon="mdi-information"
        >
          <v-alert-title class="text-body-2" style="line-height: 1.7">
            This indicator is only available at Local Authority and higher
            geographic levels.
          </v-alert-title>
          Due to limitations in the source data and methodology, this indicator
          cannot be accurately apportioned to smaller areas. While the Local
          Authority-level data is robust within its intended scope, there is no
          reliable way to show what the numbers would look like for smaller
          neighbourhoods. Attempting to apportion the figures may create a
          misleading impression of accuracy.
        </v-alert>

        <!-- Indicator timepoints -->
        <v-card-actions class="px-0">
          <v-autocomplete
            v-model="queryParameters.timepoints"
            label="Time point"
            :items="timepoints"
            variant="outlined"
            :no-data-text="
              loadingTimepoints
                ? 'Loading timepoints'
                : 'No timepoints available'
            "
            :loading="loadingTimepoints"
            class="py-4"
            item-value="dateSelection"
            item-title="label"
            hide-details
            density="compact"
            rounded="0"
          />
        </v-card-actions>

        <!-- Checkboxes -->
        <div class="text-subtitle-1" style="font-weight: 400">Values</div>
        <v-card-text class="pa-0">
          <div
            @click.stop="
              queryParameters.raw_values =
                !queryParameters.raw_values ||
                selectedIndicator.aggregation_type !== 'dynamic_rate'
            "
            class="d-flex pa-1 checkbox-container"
          >
            <v-checkbox
              v-model="queryParameters.raw_values"
              label="Raw values"
              hide-details
              color="primary"
              :disabled="selectedIndicator.aggregation_type !== 'dynamic_rate'"
            />
          </div>
          <div
            v-if="selectedIndicator.aggregation_type === 'dynamic_rate'"
            @click.stop="queryParameters.rates = !queryParameters.rates"
            class="d-flex pa-1 checkbox-container"
          >
            <v-checkbox
              v-model="queryParameters.rates"
              label="Rates"
              hide-details
              color="primary"
            />
          </div>
        </v-card-text>
      </v-card-text>
      <DateRangePicker
        v-model:dialogProp="pickDateRange"
        @setRange="setCustomTimeRange"
      />

      <DynamicDialog
        :show="removeIndicatorDialog"
        @dialogOk="removeIndicator()"
        @dialogCancel="removeIndicatorDialog = false"
        @update:showDyamicDialog="(state) => (removeIndicatorDialog = state)"
        max-width="450"
        okBtnColor="error"
        cancelBtnColor="none"
        cancelBtnVariant="text"
      >
        <template v-slot:title>Remove indicator</template>
        <template v-slot:content>
          Are you sure you want to remove this indicator from the query?
        </template>
        <template v-slot:okBtnTitle>remove indicator</template>
      </DynamicDialog>
    </v-card>

    <!-- Areas -->
    <DatastoreQueryBuilderAreas
      ref="areas"
      v-model:areas="selectedAreas"
      :selectedAreasLength="selectedAreasLength"
    />

    <!-- Data Format and Options -->
    <v-card ref="dataFormat" class="my-4 main">
      <!-- Header -->
      <v-card-title class="cardTitle border-b-thin border-b-md pa-0 pr-4">
        <v-card-actions>
          <v-icon
            :color="isDataFormatOptionsValid ? 'green' : '#FB8C00'"
            class="ma-2"
          >
            {{ isDataFormatOptionsValid ? "mdi-check-circle" : "mdi-alert" }}
          </v-icon>
          <div>Data Format & Options</div>
        </v-card-actions>
      </v-card-title>

      <!-- Content under the panel -->
      <v-card-text class="py-2">
        <v-select
          v-model="queryParameters.data_format"
          :items="[
            { title: 'Stacked/long format', value: 'stacked' },
            { title: 'Wide format', value: 'wide' },
          ]"
          label="Data formatting"
          variant="outlined"
          density="compact"
          rounded="0"
          hide-details="true"
        ></v-select>
      </v-card-text>
      <v-card-text class="py-2">
        <!-- Area codes -->
        <div
          @click.stop="queryParameters.area_code = !queryParameters.area_code"
          class="d-flex pa-1 checkbox-container"
        >
          <v-checkbox
            v-model="queryParameters.area_code"
            label="Include area code"
            hide-details
            color="primary"
          />
        </div>

        <!-- Unit labels -->
        <div
          @click.stop="
            queryParameters.unit_labels = !queryParameters.unit_labels
          "
          class="d-flex pa-1 checkbox-container"
        >
          <v-checkbox
            v-model="queryParameters.unit_labels"
            label="Include unit labels"
            hide-details
            color="primary"
          />
        </div>

        <!-- National comparator -->
        <div
          @click.stop="
            queryParameters.national_comparators =
              !queryParameters.national_comparators
          "
          class="d-flex pa-1 checkbox-container"
        >
          <v-checkbox
            v-model="queryParameters.national_comparators"
            label="Include national comparator"
            hide-details
            color="primary"
          />
        </div>
      </v-card-text>
    </v-card>

    <!-- Query -->
    <v-card class="my-4 main">
      <!-- Header -->
      <v-card-title class="cardTitle border-b-thin border-b-md pa-0 pr-4">
        <v-card-actions>
          <v-icon :color="isQueryComplete ? 'green' : '#FB8C00'" class="ma-2">
            {{ isQueryComplete ? "mdi-check-circle" : "mdi-alert" }}
          </v-icon>
          <div class="font-weight-medium">Your Query</div>
        </v-card-actions>
      </v-card-title>

      <v-card-text class="text-left pa-4">
        <!-- Alert -->
        <v-alert v-if="!isQueryComplete" color="warning">
          <div style="font-weight: 500">There are issues with your query:</div>
          <ul>
            <li v-if="!isAuthorisationValid">
              <a href="#" @click.prevent="scrollToSection('authorisation')"
                >Authorisation</a
              >
            </li>
            <li v-if="!isSelectedAreasComplete">
              <a href="#" @click.prevent="scrollToSection('areas')">Areas</a>
            </li>
            <li v-if="!isIndicatorComplete">
              <a href="#" @click.prevent="scrollToSection('indicator')"
                >Indicator</a
              >
            </li>
            <li v-if="!isDataFormatOptionsValid">
              <a href="#" @click.prevent="scrollToSection('dataFormat')"
                >Data Format & Options</a
              >
            </li>
          </ul>
          Please check and complete the fields above to finish building your
          query.
        </v-alert>

        <div v-else icon="mdi-check-circle" class="text caption">
          Your query is ready! Copy the HTTP API query below and use it in your
          preferred tool to access the data. Happy querying!

          <!-- Section for viewing and copying the snippet -->
          <CodeBlock
            :code="codeSnippet"
            @update:languageChanged="getSnippet"
            :omitLanguages="['JSON']"
            initialLanguage="Power Query"
          />
        </div>
      </v-card-text>
    </v-card>

    <v-card class="my-4 main">
      <v-btn
        width="100%"
        height="61"
        prepend-icon="mdi-refresh"
        color="error"
        @click="resetFormDialog = true"
        variant="text"
      >
        reset form
      </v-btn>

      <DynamicDialog
        :show="resetFormDialog"
        @dialogOk="resetForm()"
        @dialogCancel="resetFormDialog = false"
        @update:showDyamicDialog="(state) => (resetFormDialog = state)"
        max-width="450"
        okBtnColor="error"
        cancelBtnColor="none"
        cancelBtnVariant="text"
      >
        <template v-slot:title>Confirm reset</template>
        <template v-slot:content>
          Are you sure you want to reset the form? This will clear all the
          information you’ve entered so far.?
        </template>
        <template v-slot:okBtnTitle>reset form</template>
      </DynamicDialog>
    </v-card>
  </v-container>
</template>

<script>
import { systemMessages } from "@/mixins/SystemMessages";
import DynamicDialog from "@/components/DynamicDialog.vue";
import DatastoreQueryBuilderAreas from "@/components/DatastoreQueryBuilderAreas.vue";
import DateRangePicker from "@/components/DateRangePicker.vue";
import { copyToClipboard } from "@/mixins/CopyToClipboard";
import CodeBlock from "@/components/CodeBlock.vue";

export default {
  mounted() {
    /**
     * this component isn't mount when parent view first loads (catalogue loaded).  If
     * user immediately searches for indicator and adds to query we need to set the indicator
     * code in queryParams object and fetch timepoints when this component is first mounted.
     */
    if (this.selectedIndicator?.id) {
      this.queryParameters.indicator = this.selectedIndicator.indicator_code;
      this.getIndicatorTimepoints();
    }
  },
  name: "DatastoreQueryBuilder",
  mixins: [systemMessages],
  components: {
    DynamicDialog,
    DatastoreQueryBuilderAreas,
    DateRangePicker,
    CodeBlock,
  },
  data: () => ({
    queryParameters: {
      indicator: null,
      area: null,
      raw_values: null,
      rates: null,
      timepoints: null,
      response_type: null,
      data_format: null,
      area_code: null,
      unit_labels: null,
    },
    selectedKey: null,
    timepoints: [],
    removeIndicatorDialog: false,
    loadingTimepoints: false,
    pickDateRange: false,
    resetFormDialog: false,
    selectedAreas: {
      customAreas: [],
      areaCodes: [],
    },
    codeSnippet: "",
    snippetType: "Axios",
    apiUrl: "api.localinsight.org/dsapi/",
    apiEndpoint: "v1/indicators/get", //this will need to be dynamic eventually to handle multiple endpoints/api versions
    skipForNow: false,

    // timepoint range
    show_start_year: true,
    show_end_year: true,
    start_year: new Date().getFullYear(),
    start_month: 0,
    end_year: new Date().getFullYear(),
    end_month: 0,
    months: {
      0: "January",
      1: "February",
      2: "March",
      3: "April",
      4: "May",
      5: "June",
      6: "July",
      7: "August",
      8: "September",
      9: "October",
      10: "November",
      11: "December",
    },
  }),
  computed: {
    apiUrlScheme() {
      if (process.env.VUE_APP_ENVIRONMENT === "dev") {
        return "http://";
      }

      return "https://";
    },
    apiUrlEnvDomain() {
      return process.env.VUE_APP_ENVIRONMENT_SUB_DOMAIN;
    },
    isAuthorisationValid() {
      return this.selectedKey;
    },
    isSelectedAreasComplete() {
      return this.selectedAreasLength;
    },
    isDataFormatOptionsValid() {
      return this.queryParameters.data_format;
    },
    isIndicatorComplete() {
      return (
        this.selectedIndicator?.id &&
        (this.queryParameters?.raw_values || this.queryParameters?.rates) &&
        this.queryParameters?.timepoints
      );
    },
    isQueryComplete() {
      return (
        this.isAuthorisationValid &&
        this.isDataFormatOptionsValid &&
        this.isIndicatorComplete &&
        this.$refs.areas.areasValid
      );
    },
    selectedIndicator: {
      get() {
        return this.$store.getters["datastore/selectedIndicator"];
      },
      set(val) {
        this.$store.commit("datastore/setSelectedIndicator", val);
      },
    },
    /* Gets the count of selected custom areas and area codes */
    selectedAreasLength() {
      return (
        this.selectedAreas?.customAreas?.length +
        this.selectedAreas?.areaCodes?.length
      );
    },
    LAOnlyIndicator: {
      get() {
        return this.selectedIndicator.lowest_show_level == 6 ? true : false;
      },
    },
  },
  methods: {
    // scroll to the section of the page
    scrollToSection(section) {
      const target = this.$refs[section];
      if (target && target.$el) {
        target.$el.scrollIntoView({ behavior: "smooth" });
      } else if (target) {
        // Fallback for non-Vuetify elements
        target.scrollIntoView({ behavior: "smooth" });
      }
    },
    getSnippet(snippetType = null) {
      this.snippetType = !snippetType ? this.snippetType : snippetType;
      if (this.snippetType === "CURL") {
        this.codeSnippet = this.curlSnippet();
      }

      if (this.snippetType === "Guzzle") {
        this.codeSnippet = this.guzzleSnippet();
      }

      if (this.snippetType === "Axios") {
        this.codeSnippet = this.axiosSnippet();
      }

      if (this.snippetType === "Python") {
        this.codeSnippet = this.pythonSnippet();
      }

      if (this.snippetType === "R Project") {
        this.codeSnippet = this.rProjectSnippet();
      }

      if (this.snippetType === "Power Query") {
        this.codeSnippet = this.powerQuerySnippet();
      }
    },
    copySnippet() {
      copyToClipboard(this.getSnippet);
      this.successPill({
        icon: "mdi-content-copy",
        title: "Copied to clipboard",
        showClose: false,
      });
    },
    async getIndicatorTimepoints() {
      this.loadingTimepoints = true;

      const response = await this.$axios
        .get(
          `/datastore/get-indicator-timepoints/${this.selectedIndicator?.id}`,
        )
        .catch((error) => {
          this.loadingTimepoints = false;
          console.error(error);
        });

      this.timepoints = response.data.timepoints;
      this.loadingTimepoints = false;
    },
    removeIndicator() {
      this.selectedIndicator = null;
      this.removeIndicatorDialog = false;
      this.timepoints = [];
    },
    setCustomTimeRange(range) {
      // push to the list if it doesn't exist
      if (
        !this.timepoints.some((timepoint) => timepoint.dateSelection === range)
      ) {
        this.timepoints.push(range);
      }

      // set the selected timepoint
      this.queryParameters.timepoints = range;

      this.pickDateRange = false;
    },
    resetForm() {
      this.selectedAreas = {
        customAreas: [],
        areaCodes: [],
      };
      this.selectedKey = null;
      this.queryParameters = {
        indicator: null,
        area: null,
        raw_values: false,
        rates: false,
        timepoints: null,
        response_type: null,
        data_format: null,
        area_code: null,
        unit_labels: null,
      };
      this.removeIndicator();

      this.resetFormDialog = false;
    },
    getApiReqestBodyParams() {
      //filter queryParameters object to remove any null entries
      let params = Object.fromEntries(
        Object.entries(this.queryParameters).filter((p) => {
          return p[1] !== null;
        }),
      );

      //get custom areas if selected
      if (this.selectedAreasLength) {
        params.area_ids = [
          ...this.selectedAreas.customAreas.map((ca) => ca.id),
          ...this.selectedAreas.areaCodes.map((code) => code.area_code),
        ].join(",");
      }

      //convert timepoints value if date range object
      if (
        typeof params.timepoints == "object" &&
        "dateSelection" in params.timepoints
      ) {
        params.timepoints = this.convertDateRangeStringToParam(
          params.timepoints.dateSelection,
        );
      }

      //picker stores previous ranges as string e.g.2019-Jan -> 2021-Jan - catch and convert
      if (
        params.timepoints != undefined &&
        /^\d{4}-\w{3}\s*->\s*\d{4}-\w{3}$/.test(params.timepoints)
      ) {
        params.timepoints = this.convertDateRangeStringToParam(
          params.timepoints,
        );
      }

      return params;
    },
    //convert a daterange component string (e.g. 2019-Jan -> 2021-Jan) into backend param e.g. 2019-01-2021-01
    convertDateRangeStringToParam(daterange) {
      const dates = daterange.replace(/\s/g, "").split("->");
      const paramString =
        new Date(dates[0]).toISOString().slice(0, 7) +
        "-" +
        new Date(dates[1]).toISOString().slice(0, 7);

      return paramString;
    },
    curlSnippet() {
      // intentionally indented like this so it looks normal in the UI/UX
      return `
curl  -X POST ${this.apiUrlScheme + this.apiUrlEnvDomain + this.apiUrl + this.apiEndpoint} \\
      -H "Content-Type: application/json" \\
      -H "Authorization: Bearer ${this.selectedKey}" \\
      -d '${JSON.stringify(this.getApiReqestBodyParams())}'
      `;
    },
    guzzleSnippet() {
      // intentionally indented like this so it looks normal in the UI/UX
      return `
$client = new GuzzleHttp\\Client();

$result = $client->request('POST', '${this.apiUrlScheme + this.apiUrlEnvDomain + this.apiUrl + this.apiEndpoint}', [
'headers' => [
  'Content-Type' => 'application/json',
  'Authorization' => 'Bearer ${this.selectedKey}'
  ],
'json' => [${JSON.stringify(
        this.getApiReqestBodyParams(),
        (key, value) => {
          //convert json booleans string representations of boolean (for consistency of formatting - backend accepts either)
          if (typeof value == "boolean") {
            return value.toString();
          }
          return value;
        },
        2,
      )
        .replace(/:\s/g, " => ")
        .replace(/[{||}]/g, "")}]
]);
      `;
    },
    axiosSnippet() {
      // intentionally indented like this so it looks normal in the UI/UX
      return `
axios.post('${this.apiUrlScheme + this.apiUrlEnvDomain + this.apiUrl + this.apiEndpoint}',
${JSON.stringify(
  this.getApiReqestBodyParams(),
  (key, value) => {
    //convert booleans in json params to string representations of boolean (keeps things consistent with other snippets)
    if (typeof value == "boolean") {
      return value.toString();
    }
    return value;
  },
  2,
)},
{
headers: {
  'Content-Type': 'application/json',
  'Authorization': 'Bearer ${this.selectedKey}'
}
})
`;
    },
    pythonSnippet() {
      // intentionally indented like this so it looks normal in the UI/UX
      return `
import requests

url = "${this.apiUrlScheme + this.apiUrlEnvDomain + this.apiUrl + this.apiEndpoint}"
payload = {
  ${JSON.stringify(
    this.getApiReqestBodyParams(),
    (key, value) => {
      //convert booleans in json params to string representations of boolean (python doesn't accept json boolean format in dictionary)
      if (typeof value == "boolean") {
        return value.toString();
      }
      return value;
    },
    2,
  )
    .replace(/[{||}]/g, "")
    .trim()}
}
headers = {
  'Content-Type': 'application/json',
  'Authorization': 'Bearer ${this.selectedKey}'
}

response = requests.post(url, json=payload, headers=headers)

      `;
    },
    rProjectSnippet() {
      // intentionally indented like this so it looks normal in the UI/UX
      return `
library(httr)

url <- "${this.apiUrlScheme + this.apiUrlEnvDomain + this.apiUrl + this.apiEndpoint}"

payload <- list(
  ${JSON.stringify(
    this.getApiReqestBodyParams(),
    (key, value) => {
      //convert booleans in json params to string representations of boolean (keeps things consistent with other snippets)
      if (typeof value == "boolean") {
        return value.toString();
      }
      return value;
    },
    2,
  )
    .replace(/:\s/g, " = ")
    .replace(/[{||}]/g, "")
    .trim()}
)

response <- POST(
  url,
  body    = payload,
  encode  = "json",
  add_headers(
    "Content-Type"  = "application/json",
    "Authorization" = "Bearer ${this.selectedKey}"
  )
)
`;
    },
    powerQuerySnippet() {
      const params = this.getApiReqestBodyParams();
      const paramOutput = Object.entries(params)
        .map(([key, val]) => `${key} = "${val}"`)
        .join(",\n      ");
      //indentation for UI display
      return `
let
    url = "${this.apiUrlScheme + this.apiUrlEnvDomain + this.apiUrl + this.apiEndpoint}",
    
    payload = Json.FromValue([
      ${paramOutput}
    ]),

    response = Json.Document(Web.Contents(url,
        [
            Content = payload,
            Headers = [
                #"Content-Type" = "application/json",
                #"Authorization" = "Bearer ${this.selectedKey}"
            ]
        ]
    )),

    dataList = 
        if response is list then
            response
        else if Record.HasFields(response, "data") then
            response[data]
        else
            {response},

    expandedTable = 
        Table.ExpandRecordColumn(
            Table.FromList(dataList, Splitter.SplitByNothing()), 
            "Column1", 
            List.Union(List.Transform(dataList, each Record.FieldNames(_)))
        ),

    // Define the desired columns and types
    targetColumnsTypes = [
        rate = type number,
        value = type number,
        timepoint = type date
    ],

    existingColumns = Table.ColumnNames(expandedTable),

    // Select only the mappings for columns that actually exist
    existingTypeMappings = Record.SelectFields(targetColumnsTypes, List.Intersect({Record.FieldNames(targetColumnsTypes), existingColumns})),

    // Check if there are any columns to transform
    changedTypes = 
        if Record.FieldCount(existingTypeMappings) = 0 then
            expandedTable
        else
            Table.TransformColumnTypes(
                expandedTable,
                List.Transform(
                    Record.FieldNames(existingTypeMappings),
                    each {_, Record.Field(existingTypeMappings, _)}
                )
            )
in
    changedTypes      
`;
    },
  },
  watch: {
    queryParameters: {
      handler() {
        this.getSnippet();
      },
      deep: true,
    },
    selectedIndicator: {
      handler(newVal, oldVal) {
        //set raw values and rates checkboxes
        if (newVal?.aggregation_type !== "dynamic_rate") {
          //not dynamic rate or null
          this.queryParameters.raw_values = true;
          this.queryParameters.rates = null;
        } else {
          this.queryParameters.raw_values = null;
        }

        // indicator has changed
        if (newVal && newVal?.id !== oldVal?.id) {
          this.getIndicatorTimepoints();

          this.queryParameters.indicator = newVal.indicator_code;
        }
      },
      deep: true,
    },
    "queryParameters.timepoints": function (newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        if (newVal === "date_range") {
          // open date picker
          this.pickDateRange = !this.pickDateRange;

          // reset these
          this.queryParameters.timepoints = null;
        }
      }
    },
    skipForNow(newVal) {
      if (newVal) {
        this.selectedKey = "YOUR_API_KEY";
      } else {
        this.selectedKey = null;
      }
    },
    selectedAreas: {
      handler() {
        this.getSnippet();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.circle {
  width: 11px;
  height: 11px;
  border-radius: 10px;
}

.button-container {
  display: flex;
  justify-content: flex-end;
}
.caption {
  text-align: left;
  color: #4d4d4d;
}
.main {
  width: calc(100% - 32px);
  max-width: 800px;
  margin: 0 auto;
  border-radius: 8px;
}
@media (min-width: 600px) {
  .main {
    width: auto;
  }
}
.select-key {
  color: #4d4d4d;
}

/* base box */
.checkbox-container {
  border-left: solid 1px rgba(0, 0, 0, 0.12);
  border-right: solid 1px rgba(0, 0, 0, 0.12);
  border-bottom: solid 1px rgba(0, 0, 0, 0.12);
  cursor: pointer;
}

/* first item: add top border + top corners */
.checkbox-container:first-child {
  border-top: solid 1px rgba(0, 0, 0, 0.12);
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

/* last item: bottom corners */
.checkbox-container:last-child {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}
.grey-background {
  background-color: #e0e0e0; /* Light grey background */
}
</style>
